/**
 * Excel Export/Import Service
 * Handles exporting account data to Excel and importing from Excel files
 * Supports the Book of Accounts format from the Excel analysis
 */
/**
 * Export enhanced accounts to Excel format
 * Creates a workbook with multiple sheets for different account tracking features
 */
export async function exportAccountsToExcel(accounts, fileName = 'Book_of_Accounts.json') {
    try {
        // Prepare data in Excel-friendly format
        const workbook = {
            sheets: {
                'Account Ledger': accounts.map((acc) => ({
                    'Account Name': acc.name,
                    'Account Type': acc.type,
                    'Balance': acc.balance,
                    'Currency': acc.currency,
                    'Status': acc.isActive ? 'Active' : 'Inactive',
                    'Created Date': acc.createdAt,
                    'Last Updated': acc.updatedAt,
                })),
                'Financial Details': accounts
                    .filter((acc) => acc.financialDetails)
                    .map((acc) => ({
                    'Account Name': acc.name,
                    'Financial Status': acc.financialDetails?.financialStatus,
                    'Next Payable Date': acc.financialDetails?.nextPayableDate,
                    'Next Payment USD': acc.financialDetails?.nextPaymentUSD,
                    'Next Payment INR': acc.financialDetails?.nextPaymentINR,
                    'Total Balance USD': acc.financialDetails?.totalBalanceUSD,
                    'Total Balance INR': acc.financialDetails?.totalBalanceINR,
                    'Payable Period Start': acc.financialDetails?.payablePeriodStart,
                    'Payable Period End': acc.financialDetails?.payablePeriodEnd,
                })),
                'Billing Schedule': accounts
                    .filter((acc) => acc.billingSchedule)
                    .map((acc) => ({
                    'Account Name': acc.name,
                    'Billing Interval': acc.billingSchedule?.billingInterval,
                    'Priority': acc.billingSchedule?.priority,
                    'Urgency': acc.billingSchedule?.urgency,
                    'Monday Doc Reference': acc.billingSchedule?.mondayDocReference,
                })),
                'Categorization': accounts
                    .filter((acc) => acc.categorization)
                    .map((acc) => ({
                    'Account Name': acc.name,
                    'Category': acc.categorization?.category,
                    'Sub-Category': acc.categorization?.subCategory,
                    'Account Owner Group': acc.categorization?.accountOwnerGroup,
                    'Account Type Category': acc.categorization?.accountTypeCategory,
                })),
            },
        };
        // Convert to JSON and download (browser environment)
        if (typeof window !== 'undefined') {
            const dataStr = JSON.stringify(workbook, null, 2);
            const dataBlob = new Blob([dataStr], { type: 'application/json' });
            const url = URL.createObjectURL(dataBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        }
    }
    catch (error) {
        console.error('Error exporting accounts to Excel:', error);
        throw error;
    }
}
/**
 * Export accounts as CSV for Excel compatibility
 * Creates a CSV file that can be opened in Excel
 */
export async function exportAccountsAsCSV(accounts, fileName = 'Book_of_Accounts.csv') {
    try {
        // Create CSV content with account ledger data
        const headers = [
            'Account Name',
            'Account Type',
            'Balance',
            'Currency',
            'Status',
            'Financial Status',
            'Next Payment USD',
            'Next Payment INR',
            'Total Balance USD',
            'Total Balance INR',
            'Billing Interval',
            'Priority',
            'Urgency',
            'Category',
            'Sub-Category',
            'Account Owner Group',
            'Account Type Category',
        ];
        const rows = accounts.map((acc) => [
            acc.name,
            acc.type,
            acc.balance,
            acc.currency,
            acc.isActive ? 'Active' : 'Inactive',
            acc.financialDetails?.financialStatus || '',
            acc.financialDetails?.nextPaymentUSD || '',
            acc.financialDetails?.nextPaymentINR || '',
            acc.financialDetails?.totalBalanceUSD || '',
            acc.financialDetails?.totalBalanceINR || '',
            acc.billingSchedule?.billingInterval || '',
            acc.billingSchedule?.priority || '',
            acc.billingSchedule?.urgency || '',
            acc.categorization?.category || '',
            acc.categorization?.subCategory || '',
            acc.categorization?.accountOwnerGroup || '',
            acc.categorization?.accountTypeCategory || '',
        ]);
        const csvContent = [
            headers.join(','),
            ...rows.map((row) => row.map((cell) => {
                // Escape quotes and wrap in quotes if contains comma
                const cellStr = String(cell || '').replace(/"/g, '""');
                return cellStr.includes(',') ? `"${cellStr}"` : cellStr;
            }).join(',')),
        ].join('\n');
        // Download CSV file
        if (typeof window !== 'undefined') {
            const dataBlob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(dataBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        }
    }
    catch (error) {
        console.error('Error exporting accounts as CSV:', error);
        throw error;
    }
}
/**
 * Import accounts from CSV file
 * Parses CSV and returns account data
 */
export async function importAccountsFromCSV(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const csv = event.target?.result;
                const lines = csv.split('\n');
                const headers = lines[0].split(',').map((h) => h.trim());
                const accounts = lines.slice(1).map((line) => {
                    if (!line.trim())
                        return null;
                    // Simple CSV parsing (doesn't handle all edge cases)
                    const values = line.split(',').map((v) => v.trim().replace(/^"|"$/g, ''));
                    const obj = {};
                    headers.forEach((header, index) => {
                        obj[header] = values[index] || '';
                    });
                    return obj;
                }).filter(Boolean);
                resolve(accounts);
            }
            catch (error) {
                reject(new Error(`Failed to parse CSV: ${error instanceof Error ? error.message : String(error)}`));
            }
        };
        reader.onerror = () => {
            reject(new Error('Failed to read file'));
        };
        reader.readAsText(file);
    });
}
/**
 * Import accounts from JSON file (workbook format)
 */
export async function importAccountsFromJSON(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const json = JSON.parse(event.target?.result);
                resolve(json);
            }
            catch (error) {
                reject(new Error(`Failed to parse JSON: ${error instanceof Error ? error.message : String(error)}`));
            }
        };
        reader.onerror = () => {
            reject(new Error('Failed to read file'));
        };
        reader.readAsText(file);
    });
}
/**
 * Generate Excel export template with sample data
 * Helps users understand the expected format for importing
 */
export function generateExcelTemplate() {
    const template = {
        sheets: {
            'Account Ledger': [
                {
                    'Account Name': 'Chase Checking',
                    'Account Type': 'checking',
                    'Balance': 5000,
                    'Currency': 'USD',
                    'Status': 'Active',
                    'Created Date': new Date().toISOString(),
                },
                {
                    'Account Name': 'Student Loan',
                    'Account Type': 'credit',
                    'Balance': -25000,
                    'Currency': 'USD',
                    'Status': 'Active',
                    'Created Date': new Date().toISOString(),
                },
            ],
            'Financial Details': [
                {
                    'Account Name': 'Chase Checking',
                    'Financial Status': 'budgeted',
                    'Next Payable Date': '2025-12-31',
                    'Next Payment USD': 450,
                    'Next Payment INR': 3240000,
                    'Total Balance USD': 5000,
                    'Total Balance INR': 36000000,
                },
            ],
            'Billing Schedule': [
                {
                    'Account Name': 'Chase Checking',
                    'Billing Interval': 'monthly',
                    'Priority': 'target',
                    'Urgency': 'urgent',
                    'Monday Doc Reference': 'https://monday.com/...',
                },
            ],
            'Categorization': [
                {
                    'Account Name': 'Chase Checking',
                    'Category': 'Banking',
                    'Sub-Category': 'Checking Accounts',
                    'Account Owner Group': 'Family',
                    'Account Type Category': 'expense_account',
                },
            ],
            'Time Tracking': [
                {
                    'Account Name': 'Chase Checking',
                    'Project Name': 'Account Reconciliation',
                    'Started By': 'User Name',
                    'Start Date': new Date().toISOString().split('T')[0],
                    'Duration': '00:30:00',
                },
            ],
        },
    };
    return JSON.stringify(template, null, 2);
}
/**
 * Validate imported account data
 * Ensures required fields are present
 */
export function validateImportedData(data) {
    const errors = [];
    data.forEach((row, index) => {
        if (!row['Account Name']) {
            errors.push(`Row ${index + 2}: Account Name is required`);
        }
        if (!row['Account Type']) {
            errors.push(`Row ${index + 2}: Account Type is required`);
        }
        if (row['Balance'] && isNaN(parseFloat(row['Balance']))) {
            errors.push(`Row ${index + 2}: Balance must be a number`);
        }
    });
    return {
        valid: errors.length === 0,
        errors,
    };
}
